import { type ReactNode, useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import type { TripView } from "./api.ts";
import { ASK_AI, type EditButton, promptLead, promptWith } from "./ask-ai.ts";
import { tabWithin } from "./Dialog.tsx";
import { useGrow, useMedia } from "./hooks.ts";
import { lockedWords } from "./Spots.tsx";
import { Button, Icon } from "./souvenir.ts";
import { CopyButton, ItemIcon } from "./ui.tsx";

// Change with your AI (DESIGN.md, "Change it with your AI, right where I'd look"): a card's "Change with your
// AI" (on the small card, its icon alone) and a view's "Add" open one sheet, a sheet from the foot on a phone and
// a popover beside the button on a computer: what the web does right here, each a button doing it on the card or
// view; then the prompt to copy, already naming the trip and the thing, with likely asks as chips that fill it,
// and Copy. A read-only trip's sheet says why nothing changes. The words are ask-ai.ts's.

export function AskAi<E extends string>({
  trip,
  thing,
  edits,
  asks,
  onEdit,
  look,
}: {
  trip: TripView;
  // What the prompt is for: a card's title; a view's prompt names the trip alone.
  thing?: string;
  edits: EditButton<E>[];
  asks: string[];
  // A button chosen: the sheet has closed, and the card or view does it.
  onEdit: (edit: E) => void;
  // A card's button in words; the small card's, its icon; a view's Add on a computer, and on a phone floating
  // above the tabs.
  look: "card" | "icon" | "add" | "float";
}) {
  const [open, setOpen] = useState(false);
  const opener = useRef<HTMLButtonElement>(null);
  const label = look === "add" || look === "float" ? ASK_AI.add : ASK_AI.card;
  return (
    <>
      {look === "card" ? (
        <button
          type="button"
          ref={opener}
          className="sv-btn sv-btn--quiet sv-btn--sm tm-ask-ai-button"
          aria-haspopup="dialog"
          onClick={() => setOpen(true)}
        >
          <ItemIcon name="sparkle" size={20} />
          <span>{label}</span>
        </button>
      ) : look === "icon" ? (
        <button
          type="button"
          ref={opener}
          className="tm-peek-ai"
          aria-label={label}
          title={label}
          aria-haspopup="dialog"
          onClick={() => setOpen(true)}
        >
          <ItemIcon name="sparkle" size={18} />
        </button>
      ) : look === "float" ? (
        <button
          type="button"
          ref={opener}
          className="tm-add-float"
          aria-haspopup="dialog"
          onClick={() => setOpen(true)}
        >
          <Icon name="plus" size={20} />
          <span className="callout">{label}</span>
        </button>
      ) : (
        <button
          type="button"
          ref={opener}
          className="sv-btn sv-btn--secondary sv-btn--sm tm-add-button"
          aria-haspopup="dialog"
          onClick={() => setOpen(true)}
        >
          <Icon name="plus" size={20} />
          <span>{label}</span>
        </button>
      )}
      {open && (
        <AskSheet
          trip={trip}
          {...(thing === undefined ? {} : { thing })}
          edits={edits}
          asks={asks}
          opener={opener}
          onClose={() => setOpen(false)}
          onEdit={(e) => {
            setOpen(false);
            onEdit(e);
          }}
        />
      )}
    </>
  );
}

function AskSheet<E extends string>({
  trip,
  thing,
  edits,
  asks,
  opener,
  onClose,
  onEdit,
}: {
  trip: TripView;
  thing?: string;
  edits: EditButton<E>[];
  asks: string[];
  opener: React.RefObject<HTMLButtonElement | null>;
  onClose: () => void;
  onEdit: (edit: E) => void;
}) {
  const dialog = useRef<HTMLDialogElement>(null);
  const titleId = useId();
  const wide = useMedia("(min-width: 900px)");
  const locked = lockedWords(trip);
  const lead = promptLead(trip.title, thing);
  const [text, setText] = useState(lead);
  const growText = useGrow(text);
  const [copied, setCopied] = useState(false);
  const copiedTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => () => clearTimeout(copiedTimer.current ?? undefined), []);
  // The latest close, for the key listener set up once; a button chosen closes without putting the focus back
  // on the opener, since what it opens takes it.
  const close = useRef(onClose);
  close.current = onClose;
  const refocus = useRef(true);

  // Open before it's placed (below): placed, it's measured.
  useLayoutEffect(() => {
    const el = dialog.current;
    if (!el) return;
    el.showModal();
    el.querySelector<HTMLElement>(".tm-ask-title")?.focus();
    const onKey = (e: KeyboardEvent) => {
      const open = document.querySelectorAll("dialog[open]");
      if (open[open.length - 1] !== el) return;
      if (e.key === "Escape") {
        // The sheet only: never the card or view under it.
        e.preventDefault();
        e.stopImmediatePropagation();
        close.current();
      } else tabWithin(el, e);
    };
    addEventListener("keydown", onKey, true);
    // A tap outside it (on its backdrop: the dialog itself, outside its box) closes it.
    const onTap = (e: MouseEvent) => {
      if (e.target !== el) return;
      const r = el.getBoundingClientRect();
      if (e.clientX < r.left || e.clientX > r.right || e.clientY < r.top || e.clientY > r.bottom) close.current();
    };
    el.addEventListener("click", onTap);
    const back = opener.current;
    return () => {
      removeEventListener("keydown", onKey, true);
      el.removeEventListener("click", onTap);
      if (el.open) el.close();
      if (refocus.current && back?.isConnected) back.focus({ preventScroll: true });
    };
  }, [opener]);

  // On a computer, beside its button: under it, its right edge on the button's (or its left, where that fits
  // better), above it when there's more room there; inside the window, scrolling inside where it's taller.
  const [at, setAt] = useState<{ top: number; left: number; maxHeight: number } | null>(null);
  useLayoutEffect(() => {
    if (!wide) {
      setAt(null);
      return;
    }
    const place = () => {
      const el = dialog.current;
      const from = opener.current?.getBoundingClientRect();
      if (!el || !from) return;
      const gap = 8;
      const edge = 16;
      const width = el.offsetWidth;
      const height = el.scrollHeight;
      const below = innerHeight - from.bottom - gap - edge;
      const above = from.top - gap - edge;
      const down = below >= Math.min(height, 360) || below >= above;
      const room = down ? below : above;
      const shown = Math.min(height, room);
      const right = from.right - width;
      const left = Math.min(Math.max(edge, right >= edge ? right : from.left), innerWidth - width - edge);
      setAt({ left, top: down ? from.bottom + gap : from.top - gap - shown, maxHeight: room });
    };
    place();
    addEventListener("resize", place);
    return () => removeEventListener("resize", place);
  }, [wide, opener]);

  const choose = (e: E) => {
    refocus.current = false;
    // Closed at once, so what's under it can take the focus (or open a file picker) in this same tap.
    dialog.current?.close();
    onEdit(e);
  };
  const filled = (ask: string) => promptWith(lead, ask);
  let body: ReactNode;
  if (locked)
    body = (
      <div className="tm-ask-locked" role="note">
        <p className="callout tm-ask-line">{locked.text}</p>
        <p className="callout tm-muted tm-ask-line">{locked.more}</p>
      </div>
    );
  else
    body = (
      <>
        {edits.length > 0 && (
          <section className="tm-ask-part" aria-labelledby={`${titleId}-here`}>
            <h3 id={`${titleId}-here`} className="caption tm-ask-label">
              {ASK_AI.here}
            </h3>
            <div className="tm-ask-edits">
              {edits.map((e) => (
                <Button key={e.edit} variant="secondary" size="sm" icon={e.icon} onClick={() => choose(e.edit)}>
                  {e.label}
                </Button>
              ))}
            </div>
          </section>
        )}
        <section className="tm-ask-part" aria-labelledby={`${titleId}-ask`}>
          <h3 id={`${titleId}-ask`} className="caption tm-ask-label">
            {edits.length > 0 ? ASK_AI.ask : ASK_AI.askAlone}
          </h3>
          {asks.length > 0 && (
            <ul className="tm-ask-chips" aria-label="Likely asks">
              {asks.map((a) => {
                const on = text === filled(a);
                return (
                  <li key={a} className="tm-ask-chip-item">
                    <button
                      type="button"
                      className={`sv-chip sv-chip--pick tm-ask-chip${on ? " is-selected" : ""}`}
                      aria-pressed={on}
                      onClick={() => {
                        setText(on ? lead : filled(a));
                        setCopied(false);
                      }}
                    >
                      {a}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
          <textarea
            ref={growText}
            className="sv-field-input tm-ask-prompt"
            aria-label={ASK_AI.promptLabel}
            aria-describedby={`${titleId}-hint`}
            rows={3}
            value={text}
            onChange={(e) => {
              setText(e.target.value);
              setCopied(false);
            }}
          />
          <p id={`${titleId}-hint`} className="caption tm-muted">
            {ASK_AI.askHint}
          </p>
          <div className="tm-ask-copy">
            <CopyButton
              text={text}
              variant="primary"
              size="md"
              label={ASK_AI.copy}
              onCopied={() => {
                setCopied(true);
                clearTimeout(copiedTimer.current ?? undefined);
                copiedTimer.current = setTimeout(() => setCopied(false), 6000);
              }}
            />
            <p className="callout tm-ask-copied" role="status">
              {copied ? ASK_AI.copied : ""}
            </p>
          </div>
        </section>
      </>
    );

  // On the page itself, in the top layer: never inside what it was opened from (made inert under a form, say).
  return createPortal(
    <dialog
      ref={dialog}
      className={`tm-ask-sheet${wide ? " is-popover" : " is-sheet"}`}
      aria-labelledby={titleId}
      style={at ? { top: at.top, left: at.left, maxHeight: at.maxHeight } : undefined}
      // The browser's own Escape is handled above.
      onCancel={(e) => e.preventDefault()}
    >
      {!wide && <span className="tm-ask-grab" aria-hidden="true" />}
      <div className="tm-ask-head">
        <h2 id={titleId} className="title tm-ask-title" tabIndex={-1}>
          {ASK_AI.title}
        </h2>
        <button type="button" className="tm-layer-close" aria-label={ASK_AI.close} onClick={() => close.current()}>
          <Icon name="close" size={20} />
        </button>
      </div>
      {body}
    </dialog>,
    document.body,
  );
}
